import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { getApiUrl } from '../../config/api';

function SocialLogin() {
  const navigate = useNavigate();
  const { loginCompany } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const googleButtonRef = useRef(null);

  useEffect(() => {
    const loadGoogleScript = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      script.onload = () => {
        if (window.google) {
          initializeGoogleLogin();
        }
      };

      script.onerror = () => {
        console.error('Failed to load Google Sign-In API');
      };
    };

    if (!window.google) {
      loadGoogleScript();
    } else {
      initializeGoogleLogin();
    }

    return () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.cancel();
      }
    };
  }, []);

  const initializeGoogleLogin = () => {
    window.google.accounts.id.initialize({
      client_id: '859791521611-il745k9lf055oluti7s2les6nsmu5hdn.apps.googleusercontent.com',
      callback: handleGoogleResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
      context: 'signin',
      ux_mode: 'popup',
    });

    renderGoogleButton();

    setTimeout(() => {
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          console.log('One-tap not displayed:', notification.getNotDisplayedReason());
        }
      });
    }, 1000);
  };

  const renderGoogleButton = () => {
    const buttonContainer = googleButtonRef.current;
    if (window.google && window.google.accounts.id && buttonContainer) {
      window.google.accounts.id.renderButton(buttonContainer, {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: buttonContainer.offsetWidth,
      });
    }
  };

  const handleGoogleResponse = async (response) => {
    if (response.credential) {
      setIsLoading(true);
      try {
        const backendResponse = await fetch(getApiUrl('api/auth/google'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: response.credential }),
          credentials: 'include',
        });

        if (!backendResponse.ok) {
          throw new Error(`Failed to authenticate with Google: ${backendResponse.status}`);
        }

        const data = await backendResponse.json();
        loginCompany(data.employer_email || data.email);
        navigate('/companyhome');
      } catch (error) {
        console.error('Google authentication error:', error);
        alert('Failed to log in with Google. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };
  const handleGoogleLogin = () => {
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.prompt();
    } else {
      console.error('Google Sign-In API not loaded yet.');
      alert('Google Sign-In is not available right now. Please try again later.');
    }
  };
  

  // LinkedIn OAuth (OpenID Connect flow). Scopes 'openid profile email' are
  // the modern "Sign In with LinkedIn using OIDC" product — the old
  // r_liteprofile/r_emailaddress scopes were deprecated by LinkedIn in 2023.
  const handleLinkedInLogin = () => {
    const clientId = 'YOUR_LINKEDIN_CLIENT_ID'; // ← Replace with your real LinkedIn Client ID after registering the app
    const redirectUri = `${window.location.origin}/auth/linkedin/callback`;
    const state = crypto.randomUUID();

    sessionStorage.setItem('linkedin_oauth_state', state);
    sessionStorage.setItem('linkedin_login_flow', 'company'); // tells callback to use loginCompany

    const linkedInAuthUrl = new URL('https://www.linkedin.com/oauth/v2/authorization');
    linkedInAuthUrl.searchParams.append('response_type', 'code');
    linkedInAuthUrl.searchParams.append('client_id', clientId);
    linkedInAuthUrl.searchParams.append('redirect_uri', redirectUri);
    linkedInAuthUrl.searchParams.append('state', state);
    linkedInAuthUrl.searchParams.append('scope', 'openid profile email');

    window.location.href = linkedInAuthUrl.toString();
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <Separator />
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
        </div>
      </div>
      
      <div className="flex items-center justify-center gap-3">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            "h-10 w-10 rounded-md",
            "border-border hover:bg-accent hover:border-primary/20",
            "transition-all duration-200 shadow-sm hover:shadow"
          )}
          aria-label="Sign in with Google"
          onClick={handleGoogleLogin}
        >
          <img 
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/6eeaec7c748b8433b38763a968e69e291265f3958f03f5ff89e87bcfae61a444?placeholderIfAbsent=true&apiKey=011132b2f41c4af7b38e22acc88aa803"
            alt="Google"
            className="w-5 h-5"
          />
        </Button>
        
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            "h-10 w-10 rounded-md",
            "border-border hover:bg-accent hover:border-primary/20",
            "transition-all duration-200 shadow-sm hover:shadow"
          )}
          aria-label="Sign in with LinkedIn"
          onClick={handleLinkedInLogin}
        >
          <img 
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/c0b949139a9ddf4f4f5ac6973d797b4430236df90e6f8ec8343a8b4273129caa?placeholderIfAbsent=true&apiKey=011132b2f41c4af7b38e22acc88aa803"
            alt="LinkedIn"
            className="w-5 h-5"
          />
        </Button>
      </div>
    </div>
  );
}

export default SocialLogin;
